import dayjs from "dayjs";
import { defineStore, storeToRefs } from "pinia";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "../assets/icons/Icon";
import { useUser } from "../composition-functions/user";
import { UserType } from "../composition-functions/user/UserType";
import { config } from "../config/config";
import { UserPermission } from "../enums/UserPermission";
import { setToastMessage, setToastMessageLoading } from "../util/SweetalertHelper";
import { MenuItemType } from "./enum.menuItemType";
import { DashboardSection } from "./model.dashboard";

export const useAdminDashboard = defineStore("adminDashboardStore", () => {
  const { t } = useI18n();
  const userStore = useUser();
  const { userType, permissions } = storeToRefs(userStore);

  async function updateCss() {
    setToastMessageLoading();

    try {
      const response = await fetch(`${config.eassistantProductionUrl}/MandantFile/UpdateWidgetCssFiles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        cache: "no-store",
      });

      if (!response.ok) {
        setToastMessage({ icon: "error", title: t("message.error") });
        return;
      }

      setToastMessage({ icon: "success", title: t("message.successful") });
    } catch (_error) {
      setToastMessage({ icon: "error", title: t("message.error") });
    }
  }

  const adminDashboardConfiguration = computed((): DashboardSection[] => [
    {
      title: t("section.common"),
      isVisible: true,
      items: [
        {
          type: MenuItemType.ROUTE,
          icon: Icon.NETWORK,
          title: t("pages.mandant.list.title"),
          subtitle: t("pages.mandant.list.subtitle"),
          to: { name: "mandantsList" },
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.COLLABORATION,
          title: t("pages.collectiveGroup.list.title"),
          subtitle: t("pages.collectiveGroup.list.subtitle"),
          to: { name: "collectiveGroupList" },
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.TEAM,
          title: t("pages.globalUser.list.title"),
          subtitle: t("pages.globalUser.list.subtitle"),
          to: { name: "userGlobalList" },
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.TEAM,
          title: t("pages.user.list.title"),
          subtitle: t("pages.user.list.subtitle"),
          to: { name: "userList" },
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.BROWSER,
          title: t("pages.widgetDefinition.list.title"),
          subtitle: t("pages.widgetDefinition.list.subtitle"),
          to: { name: "widgetDefinitionsList" },
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.BROWSER,
          title: t("pages.mandantForm.title"),
          subtitle: t("pages.mandantForm.subtitle"),
          to: config.eassistantProductionUrl + "/MandantForm/Index/0",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.EMAIL,
          title: t("pages.emailTemplates.title"),
          subtitle: t("pages.emailTemplates.subtitle"),
          to: config.eassistantProductionUrl + "/Mail/Index/0",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.NETWORKING,
          title: t("pages.interface.title"),
          subtitle: t("pages.interface.subtitle"),
          to: config.eassistantProductionUrl + "/Interface/Index/0",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.CONTRACT,
          title: t("pages.releaseNotes.releaseNotes.list.title"),
          subtitle: t("pages.releaseNotes.releaseNotes.list.subTitle"),
          to: { name: "releaseNotesList" },
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.LOG,
          title: t("pages.activityLog.title"),
          subtitle: t("pages.activityLog.subtitle"),
          to: { name: "activityLogList" },
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
      ],
    },
    {
      title: t("section.tourismPortals"),
      isVisible: userType.value === UserType.SUPER_ADMIN || permissions.value.includes(UserPermission.PORTAL),
      items: [
        {
          type: MenuItemType.ROUTE,
          icon: Icon.STOPWATCH,
          title: t("pages.mandantLink.contractExires.title"),
          subtitle: t("pages.mandantLink.contractExires.subtitle"),
          to: config.eassistantProductionUrl + "/MandantLink/ContractExpires",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.CALENDAR,
          title: t("pages.mandantLink.overview.title"),
          subtitle: t("pages.mandantLink.overview.subtitle"),
          to: config.eassistantProductionUrl + "/MandantLink/Overview",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.CONTRACT,
          title: t("pages.mandantText.publishText.title"),
          subtitle: t("pages.mandantText.publishText.subtitle"),
          to: config.eassistantProductionUrl + "/MandantText/PublishText",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.BARS_CHART,
          title: t("pages.mandantStat.statistics.title"),
          subtitle: t("pages.mandantStat.statistics.subtitle"),
          to: config.eassistantProductionUrl + "/MandantStat/Statistics",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.OFFICE_MATERIAL,
          title: t("pages.mandantLink.daySummary.title"),
          subtitle: t("pages.mandantLink.daySummary.subtitle"),
          to: config.eassistantProductionUrl + "/MandantLink/DaySummary",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.OFFICE_FOLDERS,
          title: t("pages.lists.title"),
          subtitle: t("pages.lists.subtitle"),
          to: config.eassistantProductionUrl + "/Lists",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.CLIPBOARD,
          title: t("pages.mandantStat.monthlyClickReport.title"),
          subtitle: t("pages.mandantLink.monthlyClickReport.subtitle"),
          to: `${config.eassistantProductionUrl}/MandantStat/MonthlyClickReport?fromdate=${dayjs().subtract(2, "year").startOf("month").format("YYYY-MM-DD")}&todate=${dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD")}`,
          isVisible: true,
          isDisabled: false,
        },
      ],
    },
    {
      title: t("section.booked"),
      isVisible: true,
      items: [
        {
          type: MenuItemType.ROUTE,
          icon: Icon.ROOM_SERVICE,
          title: t("pages.amenity.title"),
          subtitle: t("pages.amenity.subtitle"),
          to: config.eassistantProductionUrl + "/Amenity",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.ALERT,
          title: t("pages.reservation.quarantineList.title"),
          subtitle: t("pages.reservation.quarantineList.subtitle"),
          to: config.eassistantProductionUrl + "/Reservation/QuarantineList",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.SEARCH,
          title: t("pages.bookedTracking.title"),
          subtitle: t("pages.bookedTracking.subtitle"),
          to: config.eassistantProductionUrl + "/BookedTracking",
          isVisible: true,
          isDisabled: false,
        },
      ],
    },
    {
      title: t("section.portals"),
      isVisible: userType.value === UserType.SUPER_ADMIN || permissions.value.includes(UserPermission.PORTAL),
      items: [
        {
          type: MenuItemType.ROUTE,
          icon: Icon.MENU,
          title: t("pages.portal.list.title"),
          subtitle: t("pages.portal.list.subtitle"),
          to: { name: "portalsList" },
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.MENU,
          title: t("pages.linkType.title"),
          subtitle: t("pages.linkType.subtitle"),
          to: config.eassistantProductionUrl + "/LinkType",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.MENU,
          title: t("pages.portalType.title"),
          subtitle: t("pages.portalType.subtitle"),
          to: config.eassistantProductionUrl + "/PortalType",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.MENU,
          title: t("pages.region.title"),
          subtitle: t("pages.region.subtitle"),
          to: config.eassistantProductionUrl + "/PortalRegion",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.MENU,
          title: t("pages.place.title"),
          subtitle: t("pages.place.subtitle"),
          to: config.eassistantProductionUrl + "/Place",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.MENU,
          title: t("pages.category.title"),
          subtitle: t("pages.category.subtitle"),
          to: config.eassistantProductionUrl + "/Category",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.THEMES,
          title: t("pages.spCategory.title"),
          subtitle: t("pages.spCategory.subtitle"),
          to: config.eassistantProductionUrl + "/SpCategory",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.CALENDAR,
          title: t("pages.spHolidayPeriod.title"),
          subtitle: t("pages.spHolidayPeriod.subtitle"),
          to: config.eassistantProductionUrl + "/SpHolidayPeriod",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.COLLABORATION,
          title: t("pages.spTargetGroup.title"),
          subtitle: t("pages.spTargetGroup.subtitle"),
          to: config.eassistantProductionUrl + "/SpTargetGroup",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.QUALITY,
          title: t("pages.portalQualityCriteria.title"),
          subtitle: t("pages.portalQualityCriteria.subtitle"),
          to: config.eassistantProductionUrl + "/PortalQualityCriteria",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.WAREHOUSE,
          title: t("pages.equipment.title"),
          subtitle: t("pages.equipment.subtitle"),
          to: config.eassistantProductionUrl + "/Equipment",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.ROOM_SERVICE,
          title: t("pages.portalAdditional.title"),
          subtitle: t("pages.portalAdditional.subtitle"),
          to: config.eassistantProductionUrl + "/PortalAdditional",
          isVisible: userType.value === UserType.SUPER_ADMIN,
          isDisabled: false,
        },
      ],
    },
    {
      title: t("section.voucher"),
      isVisible: userType.value === UserType.SUPER_ADMIN || permissions.value.includes(UserPermission.VOUCHER),
      items: [
        {
          type: MenuItemType.ROUTE,
          icon: Icon.THEMES,
          title: t("pages.voucher.template.list.title"),
          subtitle: t("pages.voucher.template.list.subtitle"),
          to: { name: "voucherTemplatesList" },
          isVisible: true,
          isDisabled: false,
        },
      ],
    },
    {
      title: t("section.settings"),
      isVisible: userType.value === UserType.SUPER_ADMIN,
      items: [
        {
          type: MenuItemType.ROUTE,
          icon: Icon.HOLIDAY,
          title: t("pages.holiday.title"),
          subtitle: t("pages.holiday.subtitle"),
          to: config.eassistantProductionUrl + "/Holiday/Index",
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ROUTE,
          icon: Icon.DIARY,
          title: t("pages.mandantText.title"),
          subtitle: t("pages.mandantText.subtitle"),
          to: { name: "globalMandantTexts" },
          isVisible: true,
          isDisabled: false,
        },
        {
          type: MenuItemType.ACTION,
          icon: Icon.BROWSER,
          title: t("pages.updateCSS.title"),
          subtitle: t("pages.updateCSS.subtitle"),
          onClick: updateCss,
          isVisible: true,
          isDisabled: false,
        },
      ],
    },
  ]);

  return {
    adminDashboardConfiguration,
    updateCss,
  };
});
